<template>
  <Card
    data-pdf-exclude="true"
    class="flex h-full min-w-0 w-full flex-col overflow-hidden rounded-[1.35rem] border border-[rgba(218,226,238,0.95)] bg-gradient-to-b from-white to-[#fbfcff] shadow-[0_20px_40px_rgba(15,23,42,0.05)]"
  >
    <CardHeader class="border-b border-[rgba(227,233,243,0.95)] px-6 py-5 pb-4">
      <div
        class="flex items-start justify-between gap-4 max-[640px]:flex-col max-[640px]:items-stretch"
      >
        <div>
          <CardTitle class="m-0 text-[1.2rem] font-extrabold text-[#182033]">
            Recent Activity
          </CardTitle>
          <CardDescription class="mt-[0.35rem] text-[#6e7b92]">
            {{ role === 'client' ? 'Latest updates across your tasks, tickets, reports, and services.' : 'Latest task, ticket, and report activity.' }}
          </CardDescription>
        </div>
      </div>
    </CardHeader>

    <CardContent class="flex-1 p-0">
      <div
        v-if="loading"
        class="space-y-3 p-6"
      >
        <Skeleton
          v-for="n in 5"
          :key="n"
          class="h-[4.75rem] rounded-[1.15rem] bg-stone-100"
        />
      </div>

      <div
        v-else-if="!data.length"
        class="px-6 py-6"
      >
        <div
          class="grid min-h-[16rem] place-items-center rounded-[1.15rem] border border-dashed border-[rgba(218,226,238,0.95)] bg-[#fbfcff] px-6 text-center"
        >
          <div class="max-w-md">
            <p class="text-[1rem] font-bold text-[#182033]">
              No activity yet
            </p>
            <p class="mt-2 text-[0.92rem] leading-7 text-[#6e7b92]">
              Activity will appear once tasks, tickets, or reports are created.
            </p>
          </div>
        </div>
      </div>

      <div
        v-else
        class="max-h-[520px] space-y-0 overflow-y-auto px-6 pb-2"
      >
        <article
          v-for="item in data"
          :key="item.id"
          class="flex items-start gap-3 border-t border-[rgba(235,240,247,1)] py-4 first:border-t-0"
        >
          <div
            class="mt-1 flex size-10 shrink-0 items-center justify-center rounded-[0.95rem]"
            :class="iconShellClass(item.type)"
          >
            <component
              :is="iconForType(item.type)"
              class="size-5"
              :class="iconColorClass(item.type)"
            />
          </div>

          <div class="min-w-0 flex-1">
            <div
              class="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"
            >
              <p class="font-semibold text-[#182033]">
                {{ item.title }}
              </p>
              <span class="text-[0.8rem] text-[#8a95a9]">
                {{ formatDate(item.created_at) }}
              </span>
            </div>
            <p class="mt-1 text-[0.92rem] leading-6 text-[#6e7b92]">
              {{ item.description }}
            </p>
            <div class="mt-2 flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                class="rounded-full px-3 py-[0.35rem] text-[0.76rem] font-extrabold"
                :class="badgeClass(item.type)"
              >
                {{ readableType(item.type) }}
              </Badge>
              <span
                v-if="showClientName"
                class="text-[0.82rem] text-[#6e7b92]"
              >
                {{ item.client_name }}
              </span>
            </div>
          </div>
        </article>
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  ArrowRightLeft,
  FileText,
  MessageSquarePlus,
  Ticket,
  UserPlus,
  CalendarClock,
} from "lucide-vue-next";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { DashboardActivityItem, DashboardRole } from "@/types/dashboard";

const props = defineProps<{
  data: DashboardActivityItem[];
  loading?: boolean;
  role: DashboardRole;
}>();

const showClientName = computed(() => props.role !== "client");

function formatDate(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function readableType(type: string) {
  return type.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function iconForType(type: string) {
  const key = type.toLowerCase();
  if (key.includes("task_created")) return UserPlus;
  if (key.includes("task_updated")) return ArrowRightLeft;
  if (key.includes("status")) return FileText;
  if (key.includes("ticket")) return Ticket;
  if (key.includes("report")) return FileText;
  if (key.includes("lead")) return MessageSquarePlus;
  return CalendarClock;
}

function iconShellClass(type: string) {
  const key = type.toLowerCase();
  if (key.includes("report")) return "bg-[#f2ebff]";
  if (key.includes("ticket")) return "bg-[#fff3e3]";
  if (key.includes("lead")) return "bg-[#e8f1ff]";
  if (key.includes("task_created")) return "bg-[#e6f8ec]";
  if (key.includes("task_updated")) return "bg-[#edf4ff]";
  return "bg-[#f1f5fb]";
}

function iconColorClass(type: string) {
  const key = type.toLowerCase();
  if (key.includes("report")) return "text-[#7c3aed]";
  if (key.includes("ticket")) return "text-[#b45309]";
  if (key.includes("lead")) return "text-[#2855a6]";
  if (key.includes("task_created")) return "text-[#15803d]";
  if (key.includes("task_updated")) return "text-[#2563eb]";
  return "text-[#6e7b92]";
}

function badgeClass(type: string) {
  const key = type.toLowerCase();
  if (key.includes("report")) return "bg-[#f2ebff] text-[#7c3aed]";
  if (key.includes("ticket")) return "bg-[#fff0cf] text-[#b45309]";
  if (key.includes("lead")) return "bg-[#e8f1ff] text-[#2855a6]";
  if (key.includes("task_created")) return "bg-[#e6f8ec] text-[#15803d]";
  if (key.includes("task_updated")) return "bg-[#edf4ff] text-[#2563eb]";
  return "bg-[#f1f5fb] text-[#64748b]";
}
</script>
